import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './TourSteps.css';

const TourSteps = ({ isActive, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [popupPosition, setPopupPosition] = useState({ top: '50%', left: '50%', arrow: 'none' });
  const animationFrameRef = useRef(null);
  const [tourSteps] = useState([
    { title: 'Monthly Theme', description: 'Explore this month’s wellness focus with curated content, challenges, and activities designed to support your well-being.', selector: '[data-tour="monthly-theme"]' },
    { title: 'Connect Your Fitness Device', description: 'Sync your fitness device to track activity and personalize your Woliba experience.', selector: '[data-tour="wellness-device"]' },
    { title: 'Your Daily Wellness Tip', description: 'Get a daily wellness tip to support mindfulness, reduce stress, and build small habits that improve your day.', selector: '[data-tour="wellness-tip"]' },
    { title: 'Health and Wellness Goals', description: 'Log company initiatives, view health data, and track your general wellness information.', selector: '[data-tour="health-goals"]' },
    { title: 'Experience Points', description: 'Earn points by completing activities that support your holistic wellbeing and track progress as you reach new levels. ', selector: '[data-tour="experience-points"]' },
    { title: 'Announcements', description: 'See the latest company updates, wellness initiatives, challenges, events, and more to earn rewards. ', selector: '[data-tour="announcements"]' },
    { title: 'Daily Wellness Focus', description: 'Each day highlights a wellness focus with curated activities to keep you consistent and motivated.', selector: '[data-tour="focus-cards"]' },
    { title: 'Track Your Workouts', description: 'Log workouts, track your daily activity, and stay on top of your fitness progress — all while earning points.', selector: '[data-tour="workout-section"]' },
    { title: 'Personalized Wellness Hub', description: 'Tell us what matters most to you, and we’ll build a wellness hub around your goals — with personalized resources like videos, recipes, articles, and more', selector: '[data-tour="wellness-hub"]' }
  ]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      cleanup();
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    cleanup();
    onComplete();
  };

  const cleanup = () => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }
    document.querySelectorAll('.tour-highlight').forEach((el) => {
      el.classList.remove('tour-highlight');
    });
  };

  const calculatePopupPosition = (element, stepIndex) => {
    if (!element) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', arrow: 'none' };

    const rect = element.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const popupHeight = 250;
    const popupWidth = 340;
    let top, left, transform, arrow;

    if (stepIndex === 0 || stepIndex === 1) {
      top = rect.bottom + 20;
      left = rect.left + (rect.width / 2);
      transform = 'translateX(-50%)';
      arrow = 'top';
    } else if (stepIndex === 2 || stepIndex === 3) {
      top = rect.top;
      left = rect.right + 20;
      transform = 'none';
      arrow = 'left';
    } else if (stepIndex === 4) {
      top = rect.top;
      left = rect.left - popupWidth - 80;
      transform = 'none';
      arrow = 'right';
    } else if (stepIndex === 5 || stepIndex === 6 || stepIndex === 7) {
      top = rect.bottom + 10;
      left = rect.left + (rect.width / 2);
      transform = 'translateX(-50%)';
      arrow = 'top';
    } else if (stepIndex === 8) {
      top = rect.top - popupHeight + 20;
      left = rect.left + (rect.width / 2);
      transform = 'translateX(-50%)';
      arrow = 'bottom';
    }

    if (left < 20) left = 20;
    if (left + popupWidth > windowWidth) left = windowWidth - popupWidth - 20;
    if (top < 20) top = 20;
    if (top + popupHeight > windowHeight) top = windowHeight - popupHeight - 20;

    return { top: `${top}px`, left: `${left}px`, transform, arrow };
  };

  useEffect(() => {
    if (isActive && tourSteps[currentStep]) {
      cleanup();
      
      const element = document.querySelector(tourSteps[currentStep].selector);
      if (!element) return;

      element.classList.add('tour-highlight');

      const scrollBlock = (currentStep === 1 || currentStep === 5 || currentStep === 6 || currentStep === 7) ? 'start' : 'center';
      element.scrollIntoView({ behavior: 'smooth', block: scrollBlock });

      const updatePosition = () => {
        const updatedElement = document.querySelector(tourSteps[currentStep].selector);
        if (updatedElement) {
          const position = calculatePopupPosition(updatedElement, currentStep);
          setPopupPosition(position);
        }
      };

      let frameCount = 0;
      const maxFrames = 30;
      
      const checkPosition = () => {
        updatePosition();
        frameCount++;
        if (frameCount < maxFrames) {
          animationFrameRef.current = window.requestAnimationFrame(checkPosition);
        }
      };

      animationFrameRef.current = window.requestAnimationFrame(checkPosition);
    }

    return () => {
      if (!isActive) cleanup();
    };
  }, [currentStep, isActive, tourSteps]);

  if (!isActive) return null;

  const step = tourSteps[currentStep];

  return (
    <>
      <div className="tour-overlay" />
      
      <div 
        className={`tour-popup tour-arrow-${popupPosition.arrow}`}
        style={{
          top: popupPosition.top,
          left: popupPosition.left,
          transform: popupPosition.transform
        }}
      >
        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-description">{step.description}</p>
        <div className="tour-counter">
          {currentStep + 1} of {tourSteps.length}
        </div>
        <div className="tour-actions">
          <div>
            {currentStep === 0 && (
              <button onClick={handleSkip} className="tour-button tour-button-skip">
                Skip Tour
              </button>
            )}
          </div>
          <div className="tour-button-group">
            {currentStep > 0 && (
              <button onClick={handlePrevious} className="tour-button tour-button-previous">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M10 13L5 8l5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Previous
              </button>
            )}
            <button onClick={handleNext} className="tour-button tour-button-next">
              {currentStep === tourSteps.length - 1 ? 'Finish Tour' : 'Next'}
              {currentStep !== tourSteps.length - 1 && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

TourSteps.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired
};

export default TourSteps;
